import {layer} from '@wandb/components';
import {Select} from '@wandb/weave/components/Form/Select';
import {
  STATUS_INFO,
  StatusChip,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/common/StatusChip';
import React from 'react';

import {ErrorText, SeparatedChunk} from '../../shared';
import {ERROR_BORDER_COLOR, STATUS_VALUE_MAPPING} from '../constants';
import {type AggregationFunction, type ComparisonOperator} from '../types';
import {AggregationThresholdFields} from './AggregationThresholdFields';

type StatusMetricConfigProps = {
  statusMatchValue: string;
  aggregationFunction: AggregationFunction;
  threshold: string;
  operator: ComparisonOperator;
  onStatusMatchValueChange: (value: string) => void;
  onAggregationFunctionChange: (value: AggregationFunction) => void;
  onThresholdChange: (value: string) => void;
  onOperatorChange: (operator: ComparisonOperator) => void;
  hasStatusError: boolean;
  hasThresholdError: boolean;
};

export const StatusMetricConfig = ({
  statusMatchValue,
  aggregationFunction,
  threshold,
  operator,
  onStatusMatchValueChange,
  onAggregationFunctionChange,
  onThresholdChange,
  onOperatorChange,
  hasStatusError,
  hasThresholdError,
}: StatusMetricConfigProps) => {
  const statusOptions = Object.entries(STATUS_VALUE_MAPPING).map(
    ([statusKey, numValue]) => ({
      value: numValue.toString(),
      label:
        STATUS_INFO[statusKey as keyof typeof STATUS_INFO]?.label || statusKey,
      statusType: statusKey as keyof typeof STATUS_INFO,
    })
  );

  const selectedStatusOption = statusOptions.find(
    option => option.value === statusMatchValue
  );

  return (
    <SeparatedChunk>
      <div className="font-semibold">Status configuration</div>
      <div className="mt-4 flex flex-col gap-12">
        {/* Status value selector */}
        <div>
          <div className="mb-4 text-sm text-moon-600 dark:text-wb-text-secondary">
            Match status
          </div>
          <Select
            value={selectedStatusOption}
            options={statusOptions}
            onChange={(newValue: any) =>
              onStatusMatchValueChange(newValue?.value || '0')
            }
            isClearable={false}
            placeholder="Select status..."
            menuPortalTarget={document.body}
            formatOptionLabel={(option: any) => (
              <StatusChip value={option.statusType} />
            )}
            styles={{
              container: (base: any) => ({...base, minWidth: '200px'}),
              menu: (base: any) => ({...base, zIndex: layer('menu')}),
              menuPortal: (base: any) => ({...base, zIndex: layer('menu')}),
              control: (base: any) => ({
                ...base,
                borderColor: hasStatusError
                  ? ERROR_BORDER_COLOR
                  : base.borderColor,
              }),
            }}
          />
          {hasStatusError && (
            <ErrorText>Status value must be selected</ErrorText>
          )}
        </div>

        <AggregationThresholdFields
          aggregationFunction={aggregationFunction}
          threshold={threshold}
          operator={operator}
          onAggregationFunctionChange={onAggregationFunctionChange}
          onThresholdChange={onThresholdChange}
          onOperatorChange={onOperatorChange}
          hasThresholdError={hasThresholdError}
        />
      </div>
    </SeparatedChunk>
  );
};
